/**
 * Le vocabulaire des soumissions fournisseur (V1.6-06).
 *
 * C'est ici que se joue la règle la plus importante de l'écran : **ne jamais
 * en dire plus que ce qu'on sait**. Une valeur lue par une machine se lit
 * « à vérifier », pas « confirmée ». Une disponibilité se lit « selon la
 * soumission du 7 septembre », jamais « en stock ». Un transport inconnu ne se
 * lit pas « gratuit ».
 */
import type { BadgeTone } from "@/components/StatusBadge";
import { formatDate } from "@/lib/format";

/** Le statut commercial. « Validée » ne veut pas dire « fournisseur choisi ». */
export function formatQuoteStatus(value: string): string {
  switch (value) {
    case "RECEIVED":
      return "Reçue";
    case "UNDER_REVIEW":
      return "En vérification";
    case "VALIDATED":
      return "Validée";
    case "SUPERSEDED":
      return "Remplacée";
    case "REJECTED":
      return "Refusée";
    default:
      return value;
  }
}

/**
 * Où en est la lecture. **Aucune de ces valeurs ne veut dire « validée »** :
 * la validation est un fait commercial, pas un état de lecture.
 */
export function formatExtractionState(value: string): string {
  switch (value) {
    case "NOT_STARTED":
      return "Saisie à la main";
    case "PENDING":
      return "Lecture à faire";
    case "EXTRACTING":
      return "Lecture en cours";
    case "PARSED":
      return "Lue — à vérifier";
    case "NEEDS_REVIEW":
      return "À vérifier";
    case "FAILED":
      return "Lecture impossible";
    default:
      return value;
  }
}

/** Ce que le fournisseur a répondu. Chacun dit autre chose qu'un prix de zéro. */
export function formatLineResponse(value: string): string {
  switch (value) {
    case "QUOTED":
      return "Coté";
    case "UNAVAILABLE":
      return "Indisponible";
    case "DISCONTINUED":
      return "Discontinué";
    case "NOT_QUOTED":
      return "Non coté";
    case "SUBSTITUTE":
      return "Substitution proposée";
    case "NEEDS_CLARIFICATION":
      return "À clarifier";
    case "UNEXPECTED":
      return "Non demandé";
    default:
      return value;
  }
}

/** Le transport. « Non précisé » n'est pas « gratuit ». */
export function formatFreightMode(value: string): string {
  switch (value) {
    case "AMOUNT":
      return "Facturé";
    case "INCLUDED":
      return "Compris";
    case "PICKUP":
      return "À ramasser au comptoir";
    case "UNKNOWN":
      return "Non précisé";
    default:
      return value;
  }
}

export function formatReceivedVia(value: string): string {
  switch (value) {
    case "MANUAL_UPLOAD":
      return "Document déposé";
    case "EMAIL":
      return "Courriel";
    case "MANUAL_ENTRY":
      return "Saisie (téléphone, comptoir)";
    default:
      return value;
  }
}

/** Ce que le contrôle déterministe a relevé, en toutes lettres. */
export function formatQuoteWarning(value: string): string {
  switch (value) {
    case "LINE_MATH_MISMATCH":
      return "Le sous-total ne correspond pas au calcul";
    case "TOTAL_MISMATCH":
      return "Le total du fournisseur ne correspond pas au calcul";
    case "CURRENCY_MISMATCH":
      return "Une ligne est dans une autre devise";
    case "CURRENCY_MISSING":
      return "Aucune devise n'est indiquée";
    case "QUANTITY_DIFFERS":
      return "La quantité chiffrée diffère de la quantité demandée";
    case "UNIT_DIFFERS":
      return "L'unité diffère de celle demandée";
    case "LINE_NOT_ANSWERED":
      return "Une ligne demandée est restée sans réponse";
    case "UNEXPECTED_LINE":
      return "Le fournisseur a ajouté une ligne non demandée";
    case "SUBSTITUTE_OFFERED":
      return "Le fournisseur propose un substitut";
    case "DUPLICATE_LINE":
      return "Le même article apparaît deux fois";
    case "AMBIGUOUS_PRODUCT_MATCH":
      return "Le rapprochement avec le catalogue est incertain";
    case "INCOMPLETE_PRICING":
      return "Un prix est annoncé sans quantité";
    case "FREIGHT_UNKNOWN":
      return "Les frais de transport ne sont pas indiqués";
    case "QUOTE_EXPIRED":
      return "La soumission a dépassé sa date de validité";
    case "QUOTED_WITHOUT_PRICE":
      return "Une ligne cotée n'a pas de prix unitaire";
    default:
      return value;
  }
}

/**
 * La confiance d'un rapprochement, dans le vocabulaire de l'interface.
 *
 * `null` se lit « aucun article connu » et non « certain » : l'absence de
 * réponse n'est pas une réponse.
 */
export function formatMatchConfidence(value: string | null): string {
  switch (value) {
    case "CERTAIN":
      return "Certain";
    case "LIKELY":
      return "Probable — à vérifier";
    case "UNCERTAIN":
      return "Ambigu";
    case null:
      return "Aucun article connu";
    default:
      return value ?? "Aucun article connu";
  }
}

/**
 * Une disponibilité **datée**.
 *
 * « Selon la soumission du 7 septembre », jamais « en stock » : ce que le
 * fournisseur affirmait ce jour-là n'est pas ce qu'il a aujourd'hui, et
 * l'écrire au présent ferait promettre au produit ce qu'il ne sait pas.
 */
export function formatAvailability(
  response: string,
  quoteDate: string | null,
  receivedAt: string,
): string {
  // `formatDate` sait qu'une date sans heure n'a pas de fuseau : la
  // construire avec `new Date()` donnerait la veille au Québec, et une
  // soumission du 7 s'afficherait « selon la soumission du 6 ».
  const day = formatDate(quoteDate ?? receivedAt);
  switch (response) {
    case "QUOTED":
      return `Coté selon la soumission du ${day}`;
    case "UNAVAILABLE":
      return `Indisponible selon la soumission du ${day}`;
    case "DISCONTINUED":
      return `Discontinué selon la soumission du ${day}`;
    case "NOT_QUOTED":
      return "Aucune réponse sur cette ligne";
    case "SUBSTITUTE":
      return `Substitut proposé le ${day}`;
    default:
      return formatLineResponse(response);
  }
}

/**
 * Un prix **daté**, jamais « le prix actuel ».
 *
 * V1.6-00 l'exige : un vieux prix ne doit jamais être présenté comme courant.
 * Sans date, la fonction le dit plutôt que de laisser croire.
 */
export function formatDatedPrice(
  amount: string | null,
  currency: string | null,
  quoteDate: string | null,
): string {
  if (!amount) return "—";
  const money = currency ? `${amount} ${currency}` : amount;
  if (!quoteDate) return `${money} (date inconnue)`;
  return `${money} au ${formatDate(quoteDate)}`;
}

export function quoteTone(status: string): BadgeTone {
  switch (status) {
    case "VALIDATED":
      return "available";
    case "UNDER_REVIEW":
      return "info";
    case "RECEIVED":
      return "unknown";
    case "SUPERSEDED":
      return "unknown";
    case "REJECTED":
      return "out";
    default:
      return "unknown";
  }
}

/**
 * La couleur d'une réponse de ligne.
 *
 * Une substitution est en « warning » et non en vert : le fournisseur propose
 * autre chose que ce qu'on a demandé, et rien ne dit encore que c'est
 * équivalent.
 */
export function lineResponseTone(response: string): BadgeTone {
  switch (response) {
    case "QUOTED":
      return "available";
    case "SUBSTITUTE":
      return "warning";
    case "UNAVAILABLE":
    case "DISCONTINUED":
      return "out";
    case "NOT_QUOTED":
    case "NEEDS_CLARIFICATION":
    case "UNEXPECTED":
      return "unknown";
    default:
      return "unknown";
  }
}

/** Une lecture n'est jamais peinte comme une réussite : elle attend un œil. */
export function extractionTone(state: string): BadgeTone {
  switch (state) {
    case "PARSED":
    case "NEEDS_REVIEW":
      return "warning";
    case "EXTRACTING":
    case "PENDING":
      return "info";
    case "FAILED":
      return "out";
    default:
      return "unknown";
  }
}
